import { getDefaultStore } from "jotai";
import { outOfSyncAtom } from "~/atoms/outOfSync";
import { errorReporter } from "~/error-handling";
import { toError } from "~/error-handling/errorUtils";

/**
 * Recovery for the "stale tab after a deploy" crash.
 *
 * Amplify replaces (and deletes) hashed assets on every deploy, so a tab that
 * loaded an older build will 404 when it lazily imports a chunk whose filename
 * no longer exists at origin. Vite surfaces this as a `vite:preloadError`
 * event; without handling it the app throws and the page goes blank.
 *
 * Instead of forcing an immediate reload, we flag the app out-of-sync and let
 * <OutOfSyncModal> ask the user to reload — giving them a chance to save any
 * unsaved work first. Lazy-component failures also trip the top-level
 * ErrorBoundary, whose fallback renders the same out-of-sync screen.
 *
 * The event is deliberately NOT default-prevented. Vite treats a prevented
 * event as "swallow the error" and resolves the dynamic import with
 * `undefined`, so React.lazy then throws a bare
 * "Cannot read properties of undefined (reading 'default')" — the boundary
 * can't recognise that as a chunk failure (isChunkLoadError) and falls back
 * to the generic "Try again" screen, which can never recover because React
 * caches the undefined module. Letting the real error propagate keeps the
 * recognisable "Failed to fetch dynamically imported module" message.
 */

// Report to Sentry only once per tab (avoid noise), but ALWAYS re-raise the
// modal: the user can dismiss it ("Not now") to finish work, so a later chunk
// failure must be able to re-flag — otherwise it's silently swallowed, which is
// worse than the crash we're fixing.
let alreadyReported = false;

function flagOutOfSync(error: Error): void {
  if (!alreadyReported) {
    alreadyReported = true;
    errorReporter.reportApplicationError(error, {
      level: "warning",
      tags: { operation: "chunkLoadRecovery" },
    });
  }

  getDefaultStore().set(outOfSyncAtom, true);
}

export function setupChunkLoadRecovery(): void {
  // Vite dispatches a cancelable Event named "vite:preloadError" with the
  // underlying error on `.payload`, then rethrows it (see module comment).
  window.addEventListener("vite:preloadError", (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    flagOutOfSync(
      toError(payload, "Failed to load app chunk (tab out of sync)"),
    );
  });
}
